import * as React from 'react';
import { FocusScope } from 'react-aria';
import { ScrollView, StyleSheet, TouchableWithoutFeedback, View } from 'react-native';

import { Icon } from '@gitbook/spine-icons';
import { Button, LoadingPanel } from '@gitbook/spine-react';
import { useViewport } from '@gitbook/util-react-hooks';

import { animations, useAnimateLifecycle } from '../Animate';
import { sizes, spacing, zIndexes } from '../constants';
import { usePressEscape } from '../Events';
import { InteractionLocks, useFirstResponderInteraction } from '../Interactive';
import { PortalAbsolute } from '../Portal';
import { Responsive } from '../Responsive';
import { ThemedStyleSheet, useCurrentTheme } from '../theme';

export type ModalSize = 'none' | 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge';

export interface ModalContentProps {
    /** Main title for the modal */
    title?: React.ReactNode;

    /** Longer text displayed below the title */
    subtitle?: React.ReactNode;

    /** Footer used to add buttons */
    footer?: React.ReactNode;

    /** Inner body of the modal */
    children: React.ReactNode;

    /** A title displayed in a top banner */
    bannerTitle?: string;

    /** The close modal handler. This is only needed when the modal
     * has a banner title, since in this case we want to align the
     * close button vertical within the banner */
    onClose?: () => void;
}

export interface ModalProps extends Partial<ModalContentProps> {
    testID?: string;

    /** Size of the modal, default to medium */
    size?: ModalSize;

    /** Stretch modal to the full height of the screen */
    fullHeight?: boolean;

    /** Callback when closing the modal */
    onClose?: () => void;

    /**
     * If true, pressing escape will close the modal. Defaults to true.
     */
    closeOnEscape?: boolean;

    /**
     * Inner body of the modal.
     * If a `title` is passed, it'll be passed as children to the ModalContent.
     */
    children: React.ReactNode;

    /** Customizable minWidth and minHeight - only useful for the size `none` */
    width?: number;
    minHeight?: number;

    /** Display the provided title in a top banner */
    bannerTitle?: string;
}

const ModalSizeContext = React.createContext<ModalSize>('medium');

/**
 * Screen of content to show over the main content. The modal is rendered in a portal.
 */
export function Modal(props: ModalProps): React.ReactElement {
    return (
        <PortalAbsolute>
            <ModalFrame {...props} />
        </PortalAbsolute>
    );
}

/**
 * Animated frame of the modal.
 */
export function ModalFrame(props: ModalProps): React.ReactElement {
    const {
        testID,
        onClose,
        size = 'medium',
        fullHeight,
        width,
        minHeight,
        closeOnEscape = true,
    } = props;

    const [theme, styles] = useCurrentTheme(themedStyles);
    const screenDim = useViewport();

    const maxHeight = Math.max(0, screenDim.height - 2 * spacing.xlarge);

    const backdropAnimatedRef = useAnimateLifecycle(animations.fade);
    const modalAnimatedRef = useAnimateLifecycle(animations.pop);
    const isFirstResponder = useFirstResponderInteraction(InteractionLocks.OUTSIDE_CLICK);

    usePressEscape(closeOnEscape ? onClose : undefined);

    return (
        <TouchableWithoutFeedback
            onPressIn={() => {
                // We use onPressIn to match a mousedown event and avoid closing the modal
                // when selecting text in the modal while dragging the mouse outside the modal.
                if (isFirstResponder) {
                    onClose?.();
                }
            }}
        >
            <View
                ref={backdropAnimatedRef}
                testID={testID}
                style={[
                    styles.modalBackdrop,
                    {
                        backgroundColor: theme.colors.backdrop.background,
                    },
                ]}
            >
                <FocusScope restoreFocus autoFocus>
                    <TouchableWithoutFeedback
                        onPress={() => {
                            // Do nothing: it only prevent the onPress on the backdrop to be called
                        }}
                    >
                        <Responsive.View
                            ref={modalAnimatedRef}
                            style={[
                                styles.modal,
                                {
                                    maxHeight,
                                    minHeight: minHeight
                                        ? minHeight
                                        : !fullHeight
                                          ? Math.min(modalMinHeight[size], maxHeight)
                                          : undefined,
                                    height: fullHeight ? maxHeight : undefined,
                                    maxWidth: width || modalWidths[size],
                                },
                            ]}
                            customBreakpointsStyles={{
                                // If the size is equal or smaller to the width of the modal + some spacing on the sides,
                                // we limit the width to the screen width.
                                [(width || modalWidths[size]) + 2 * spacing.medium]: {
                                    width: screenDim.width - 2 * spacing.medium,
                                },
                            }}
                            desktopStyle={[sizeStyles[size].modal]}
                            onStartShouldSetResponder={() => true}
                        >
                            <React.Suspense fallback={<LoadingPanel size="large" />}>
                                <ModalSizeContext.Provider value={size}>
                                    {props.bannerTitle ? (
                                        <ModalContent
                                            bannerTitle={props.bannerTitle}
                                            onClose={onClose}
                                            {...props}
                                        >
                                            {props.children}
                                        </ModalContent>
                                    ) : props.title ? (
                                        <ModalContent title={props.title} {...props}>
                                            {props.children}
                                        </ModalContent>
                                    ) : (
                                        props.children
                                    )}
                                </ModalSizeContext.Provider>
                            </React.Suspense>

                            {!props.bannerTitle && onClose ? (
                                <View style={styles.modalCloseButton}>
                                    <Button
                                        iconOnly
                                        kind="blank"
                                        label="Close modal"
                                        leadingIcon={Icon.X}
                                        onPress={onClose}
                                    />
                                </View>
                            ) : null}
                        </Responsive.View>
                    </TouchableWithoutFeedback>
                </FocusScope>
            </View>
        </TouchableWithoutFeedback>
    );
}

/*
 * Screen of content to show over the main content.
 */
export function ModalContent(props: ModalContentProps): React.ReactElement {
    const { bannerTitle, onClose, title, subtitle, footer, children } = props;
    const size = React.useContext(ModalSizeContext);

    return (
        <>
            <View style={[sizeStyles[size].modalHeader]}>
                {bannerTitle ? (
                    <div className="flex items-center justify-between bg-side p-6">
                        <div className="max-w-[90%] text-muted text-ui-heading-small">
                            {bannerTitle}
                        </div>

                        {onClose ? (
                            <div className="flex self-center">
                                <Button
                                    label="Close modal"
                                    kind="blank"
                                    iconOnly
                                    leadingIcon={Icon.X}
                                    onPress={onClose}
                                />
                            </div>
                        ) : null}
                    </div>
                ) : title ? (
                    <View style={[sizeStyles[size].modalHeaderTitle]}>
                        <View>
                            <span className="text-ui-heading-large">{title}</span>
                        </View>
                        {subtitle ? (
                            <View style={spacing.Margin.top200}>
                                <span className="text-ui-base">{subtitle}</span>
                            </View>
                        ) : null}
                    </View>
                ) : null}
            </View>
            <ScrollView
                style={sizeStyles[size].modalScrollBody}
                contentContainerStyle={[sizeStyles[size].modalBody]}
            >
                <React.Suspense fallback={<LoadingPanel />}>{children}</React.Suspense>
            </ScrollView>
            {footer ? <View style={[sizeStyles[size].modalFooter]}>{footer}</View> : null}
        </>
    );
}

const themedStyles = ThemedStyleSheet.create({
    modalBackdrop: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        position: 'fixed' as 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        zIndex: zIndexes.MODAL,
        alignItems: 'center',
    },
    modal: (theme) => ({
        overflow: 'hidden',
        backgroundColor: theme.colors.background.main,
        maxHeight: '80vh',
        position: 'fixed' as 'absolute',
        top: '10vh',
    }),
    modalCloseButton: {
        position: 'absolute',
        top: spacing.small,
        right: spacing.small,
    },
});

const modalMinHeight: {
    [key in ModalSize]: number;
} = {
    none: 0,
    small: 0,
    medium: 0,
    large: 0,
    xlarge: 600,
    xxlarge: 600,
};

const modalWidths: {
    [key in ModalSize]: number;
} = {
    none: 0,
    small: 400,
    medium: 520,
    large: 680,
    xlarge: 880,
    xxlarge: 1180,
};

const sizeStyles = {
    small: StyleSheet.create({
        modal: {
            width: modalWidths.small,
            borderRadius: sizes.RADIUS_XL,
        },
        modalHeader: {
            paddingTop: spacing.large,
        },
        modalHeaderTitle: {
            paddingHorizontal: spacing.xlarge,
            paddingTop: spacing.medium,
        },
        modalScrollBody: {
            marginTop: spacing.medium,
            marginBottom: spacing.xlarge,
        },
        modalBody: {
            paddingHorizontal: spacing.xlarge,
        },
        modalFooter: {
            paddingHorizontal: spacing.xlarge,
            paddingBottom: spacing.xlarge,
        },
    }),
    medium: StyleSheet.create({
        modal: {
            width: modalWidths.medium,
            borderRadius: sizes.RADIUS_XL,
        },
        modalHeader: {
            paddingTop: spacing.large,
        },
        modalHeaderTitle: {
            paddingHorizontal: spacing.xlarge,
            paddingTop: spacing.medium,
        },
        modalScrollBody: {
            marginTop: spacing.medium,
            marginBottom: spacing.xlarge,
        },
        modalBody: {
            paddingHorizontal: spacing.xlarge,
        },
        modalFooter: {
            paddingHorizontal: spacing.xlarge,
            paddingBottom: spacing.xlarge,
        },
    }),
    large: StyleSheet.create({
        modal: {
            width: modalWidths.large,
            borderRadius: sizes.RADIUS_XL,
        },
        modalHeader: {
            paddingTop: spacing.large,
        },
        modalHeaderTitle: {
            paddingHorizontal: spacing.xlarge,
            paddingTop: spacing.medium,
        },
        modalScrollBody: {
            marginTop: spacing.medium,
            marginBottom: spacing.xlarge,
        },
        modalBody: {
            paddingHorizontal: spacing.xlarge,
        },
        modalFooter: {
            paddingHorizontal: spacing.xlarge,
            paddingBottom: spacing.xlarge,
        },
    }),
    xlarge: StyleSheet.create({
        modal: {
            width: modalWidths.xlarge,
            borderRadius: sizes.RADIUS_XL,
        },
        modalHeader: {
            paddingTop: spacing.large,
        },
        modalHeaderTitle: {
            paddingHorizontal: spacing.xxlarge,
            paddingTop: spacing.medium,
        },
        modalScrollBody: {
            marginVertical: spacing.large,
        },
        modalBody: {
            paddingHorizontal: spacing.xxlarge,
        },
        modalFooter: {
            paddingHorizontal: spacing.xxlarge,
            paddingBottom: spacing.large,
        },
    }),
    xxlarge: StyleSheet.create({
        modal: {
            width: modalWidths.xxlarge,
            borderRadius: sizes.RADIUS_XL,
        },
        modalHeader: {
            paddingTop: spacing.large,
        },
        modalHeaderTitle: {
            paddingHorizontal: spacing.xxlarge,
            paddingTop: spacing.medium,
        },
        modalScrollBody: {
            marginVertical: spacing.large,
        },
        modalBody: {
            paddingHorizontal: spacing.xxlarge,
        },
        modalFooter: {
            paddingHorizontal: spacing.xxlarge,
            paddingBottom: spacing.large,
        },
    }),
    none: StyleSheet.create({
        modal: {
            borderRadius: sizes.RADIUS_XL,
        },
        modalHeader: {},
        modalHeaderTitle: {},
        modalScrollBody: {},
        modalBody: {},
        modalFooter: {},
    }),
};
